// src/components/ActivityFeed.jsx — real-time league activity log.
// Automatically logs: waivers processed, trades completed, scores posted, lineups saved.
import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { ref, onValue, limitToLast, query } from "firebase/database";

const ICONS = {
  trade:    "🤝",
  waiver:   "📋",
  score:    "⚡",
  lineup:   "📝",
  draft:    "🏏",
  system:   "📣",
};

function timeAgo(ts) {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  const hrs  = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1)  return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (hrs  < 24) return `${hrs}h ago`;
  return `${days}d ago`;
}

export function ActivityFeed({ leagueCode, maxItems = 30, compact = false }) {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    if (!leagueCode) return;
    const code = leagueCode.toUpperCase();
    const q = query(ref(db, `leagues/${code}/activity`), limitToLast(maxItems));
    const unsub = onValue(q, snap => {
      const val = snap.val() || {};
      const list = Object.values(val).sort((a, b) => b.at - a.at);
      setEvents(list);
    });
    return () => unsub();
  }, [leagueCode, maxItems]);

  if (events.length === 0) {
    return compact ? null : (
      <div style={{ padding: "16px 14px", color: "var(--text-muted)", fontSize: 13, textAlign: "center" }}>
        No activity yet — actions like trades, waivers, and scores will appear here.
      </div>
    );
  }

  return (
    <div>
      {events.map((e, i) => (
        <div key={e.at + i} style={{
          display: "flex", gap: 10, padding: compact ? "8px 14px" : "10px 16px",
          borderBottom: i < events.length - 1 ? "1px solid var(--border)" : "none",
          alignItems: "flex-start",
        }}>
          <div style={{
            width: compact ? 24 : 28, height: compact ? 24 : 28, borderRadius: "50%", flexShrink: 0,
            background: "var(--bg2)", border: "1px solid var(--border)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: compact ? 11 : 13,
          }}>
            {ICONS[e.type] || "📌"}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: compact ? 12 : 13, lineHeight: 1.4 }}>{e.text}</div>
            <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}>{timeAgo(e.at)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Helper to log an activity event from anywhere
export async function logActivity(db, leagueCode, type, text) {
  const { ref: fbRef, push, update } = await import("firebase/database");
  const code = leagueCode.toUpperCase();
  const newRef = push(fbRef(db, `leagues/${code}/activity`));
  await update(fbRef(db), {
    [`leagues/${code}/activity/${newRef.key}`]: { type, text, at: Date.now() },
  });
}
